import type { SxProps, Theme } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type ExactShiftFilterType, ShiftFilterType } from "../../staffing/MonthlyView/utils";
import { type SystemChipItem, SystemChips } from "../Clickable/TabsAndChips/SystemChips/SystemChips";

type Props = {
	allowFilterRecurringShifts?: boolean;
	allShiftsTimes: ExactShiftFilterType[];
	selectedFilters: (ExactShiftFilterType | ShiftFilterType)[];
	onSelectedFilterChange: (filter: ExactShiftFilterType | ShiftFilterType, selected: boolean) => void;
	amountOfTopPopularTimesToShow?: number;
	/**
	 * Offer exactly these windows instead of deriving the popular ones from `allShiftsTimes`. For a
	 * caller that already ranked/capped its own window set (the Monthly view counts across a whole
	 * 6-week span and caps at `MONTHLY_MAX_SHIFT_WINDOWS`) — re-deriving here would disagree with it.
	 */
	exactTimes?: ExactShiftFilterType[];
	/**
	 * Day-view chip behaviour: clicking the already-selected chip reverts to `All`. The component then
	 * emits the WHOLE new selection as a single value, so the parent replaces rather than accumulates.
	 */
	singleSelect?: boolean;
	/** Append an `Other` chip — "everything outside the windows on offer" (see `ShiftFilterType.Other`). */
	allowFilterOtherShifts?: boolean;
	/**
	 * Override the time-range chip label. Pass one when the same window is labelled elsewhere on the
	 * screen (a grid cell, a drawer) and the two have to read identically; the default is `"6AM-2PM"`.
	 */
	formatLabel?: (time: ExactShiftFilterType) => string;
	/** Keep the chips on ONE line (the caller's row has a bounded height) and scroll any overflow. */
	rootSx?: SxProps<Theme>;
	dataTestId?: string;
	getItemDataTestId?: (item: SystemChipItem<ExactShiftFilterType | ShiftFilterType>) => string;
};

export const DEFAULT_POPULAR_SHIFT_TIMES_AMOUNT = 6;

export const getPopularShiftTimes = (allShiftsTimes: ExactShiftFilterType[], amountOfTopPopularTimesToShow: number): ExactShiftFilterType[] => {
	const popularityMap: Record<string, number> = {};
	const today = DateTime.now().startOf("day");
	for (const time of allShiftsTimes) {
		const key = `${time.start.toFormat("HH")}-${time.end.toFormat("HH")}`;
		popularityMap[key] = (popularityMap[key] || 0) + 1;
	}
	const sortedTimes = Object.entries(popularityMap)
		.sort((a, b) => b[1] - a[1])
		.filter(([_, amount]) => amount > 1);
	const filteredResults = sortedTimes
		.slice(0, amountOfTopPopularTimesToShow)
		.map(([key]) => {
			const [startHour, endHour] = key.split("-").map(Number);
			return allShiftsTimes.find((time) => time.start.hour === startHour && time.end.hour === endHour);
		})
		.filter((value) => value !== undefined);
	const res = Array.from(
		new Set(
			filteredResults
				.sort((a, b) => {
					if (!a || !b) return 0;
					return a.start.hour - b.start.hour;
				})
				.map<ExactShiftFilterType>((time) => ({
					start: today.set({ hour: time?.start.hour }),
					end: today.set({ hour: time?.end.hour }),
				})),
		),
	);
	return res;
};

export const TimeFilterChips: FC<Props> = ({
	selectedFilters,
	onSelectedFilterChange,
	allShiftsTimes,
	allowFilterRecurringShifts,
	amountOfTopPopularTimesToShow = DEFAULT_POPULAR_SHIFT_TIMES_AMOUNT,
	exactTimes,
	singleSelect,
	allowFilterOtherShifts,
	formatLabel,
	rootSx,
	dataTestId = "time-filter-chips",
	getItemDataTestId,
}) => {
	const { t } = useTranslation();

	const shiftFilterChips = useMemo((): SystemChipItem<ExactShiftFilterType | ShiftFilterType>[] => {
		const times = exactTimes ?? getPopularShiftTimes(allShiftsTimes, amountOfTopPopularTimesToShow);

		// The enum members are MATCHING tokens, never labels: `ShiftFilterType.All` is the literal string
		// "All" and `recurringShift` is "Recurring Shifts", so rendering them shipped English to every
		// locale. Only `value` keeps the token; `label` is translated like the `Other` chip below.
		return [
			{
				value: ShiftFilterType.All,
				label: t("shift.time.filter.all"),
			},
			...times.map((time) => ({
				value: time,
				label: formatLabel ? formatLabel(time) : `${time.start.toFormat("ha")}-${time.end.toFormat("ha")}`,
			})),
			...(allowFilterRecurringShifts
				? [
						{
							value: ShiftFilterType.recurringShift,
							label: t("shift.time.filter.recurring"),
						},
					]
				: []),
			...(allowFilterOtherShifts ? [{ value: ShiftFilterType.Other, label: t("shift.time.filter.other") }] : []),
		];
	}, [allShiftsTimes, amountOfTopPopularTimesToShow, allowFilterRecurringShifts, exactTimes, allowFilterOtherShifts, formatLabel, t]);

	const selectedShiftFilters = useMemo(() => {
		return shiftFilterChips.filter((chip) =>
			selectedFilters.some((filter) => {
				if (typeof filter === "string" && typeof chip.value === "string") {
					return filter === chip.value;
				}
				if (typeof filter !== "string" && typeof chip.value !== "string") {
					return filter.start.toFormat("HH") === chip.value.start.toFormat("HH") && filter.end.toFormat("HH") === chip.value.end.toFormat("HH");
				}
				return false;
			}),
		);
	}, [selectedFilters, shiftFilterChips]);

	const _handleShiftFilterChange = useCallback(
		(value: SystemChipItem<ExactShiftFilterType | ShiftFilterType>, selected: boolean) => {
			if (!singleSelect) {
				onSelectedFilterChange(value.value, selected);
				return;
			}
			// Single-select: re-clicking the live chip is "clear", which is `All` — never an empty selection
			// that would leave the row with no chip lit at all.
			const isCurrentlySelected = selectedShiftFilters.some((chip) => chip.value === value.value);
			onSelectedFilterChange(isCurrentlySelected ? ShiftFilterType.All : value.value, true);
		},
		[onSelectedFilterChange, singleSelect, selectedShiftFilters],
	);

	return (
		<SystemChips
			items={shiftFilterChips}
			onChange={_handleShiftFilterChange}
			selectedItems={selectedShiftFilters}
			forceFitContent={true}
			rootSx={rootSx}
			dataTestId={dataTestId}
			getItemDataTestId={getItemDataTestId}
		/>
	);
};
